import React, { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";

import { ConvertISOToDate, IsInvalidUUID } from "../util";
import { GridActionsCellItem, GridToolbarContainer } from "@mui/x-data-grid";
import { useUserAuth } from "../contexts/authContext";
import { useMap } from "../contexts/mapContext";
import { CustomPagination } from "../customPagination";
import ResponsiveGrid from "../grid/responsiveGrid";

function EditToolbar(props) {
    const { setEditRow, setComponentDialogOpen } = props;

    const handleClick = () => {
        setEditRow({
            id: 0,
            name: "",
            component_uuid: "",
            truth_source: "",
            description: "",
            physical_location: "",
            manufacturer: "",
            model: "",
            is_sensor: false,
            sensor_type: "",
            lat: 0,
            lon: 0,
            radius: 0,
            min_angle: "",
            max_angle: "",
            monitor_uuids: []
        });
        setComponentDialogOpen(true);
    };

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={handleClick} id="createComponent" data-testid="AddRecord">
                Add Record
            </Button>
        </GridToolbarContainer>
    );
}

export function HMSComponentSettings() {
    const [components, setComponents] = useState([]);
    const [editRow, setEditRow] = useState({});
    const [componentDialogOpen, setComponentDialogOpen] = useState(false);
    const [allHimsMonitors, setAllHimsMonitors] = useState([]);

    const { handleFailedFetch } = useUserAuth();

    useEffect(() => {
        getHimsComponents();
        getHimsMonitors();

        return () => {
            setComponents([]);
            setAllHimsMonitors([]);
        };
    }, []);

    const getHimsComponents = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("/api/hms/components/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setComponents(data))
            .catch((err) => handleFailedFetch(err));
    };

    const getHimsMonitors = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("/api/hms/monitors/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setAllHimsMonitors(data))
            .catch((err) => handleFailedFetch(err));
    };

    const handleEditClick = (id) => {
        setEditRow(components.find((row) => row.id === id));
        setComponentDialogOpen(true);
    };

    const handleDeleteClick = (row) => {
        if (!confirm("Are you sure you want to Delete this component?")) {
            return;
        }

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" }
        };

        fetch(`/api/hms/component/delete/${row.id}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => getHimsComponents())
            .catch((err) => handleFailedFetch(err));
    };

    const handleComponentDialogClose = () => {
        setComponentDialogOpen(false);
        getHimsComponents();
    };

    const columns = [
        {
            field: "component_uuid",
            headerName: "UUID",
            flex: 1,
            minWidth: 300,
            editable: false
        },
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            minWidth: 150,
            editable: false
        },

        {
            field: "truth_source",
            headerName: "Truth Source",
            minWidth: 75,
            flex: 1,
            editable: false
        },
        {
            field: "is_sensor",
            headerName: "Is Sensor",
            flex: 1,
            maxWidth: 75,
            editable: false
        },
        {
            field: "description",
            headerName: "Description",
            minWidth: 250,
            flex: 1,
            editable: false
        },
        {
            field: "physical_location",
            headerName: "Location",
            minWidth: 200,
            flex: 1,
            editable: false
        },
        {
            field: "manufacturer",
            headerName: "Manufacturer",
            minWidth: 75,
            flex: 1,
            editable: false
        },
        {
            field: "model",
            headerName: "Model",
            maxWidth: 75,
            flex: 1,
            editable: false
        },
        {
            field: "date_updated",
            headerName: "Updated",
            minWidth: 175,
            type: "dateTime",
            flex: 1,
            editable: false,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 75,
            getActions: ({ row }) => {
                return [
                    <GridActionsCellItem
                        key={row.id}
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={() => handleEditClick(row.id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={row.id}
                        icon={<DeleteIcon />}
                        label="Delete"
                        className="textPrimary"
                        onClick={() => handleDeleteClick(row)}
                        color="inherit"
                    />
                ];
            }
        }
    ];

    return (
        <Box sx={{ height: 567 }}>
            <ResponsiveGrid
                primaryColumns={["name", "actions"]}
                disableVirtualization
                rows={components}
                columns={columns}
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
                pagination
                slots={{ toolbar: EditToolbar, pagination: CustomPagination }}
                slotProps={{ toolbar: { setEditRow, setComponentDialogOpen } }}
                initialState={{ pagination: { paginationModel: { pageSize: 7 } } }}
            />
            {componentDialogOpen ? (
                <ComponentDialog
                    row={editRow}
                    componentDialogOpen={componentDialogOpen}
                    handleComponentDialogClose={handleComponentDialogClose}
                    components={components}
                    allHimsMonitors={allHimsMonitors}
                />
            ) : (
                <></>
            )}
        </Box>
    );
}

export function ComponentDialog(props) {
    const row = props.row;
    const { handleFailedFetch } = useUserAuth();
    const { fetchSensors } = useMap();

    const [name, setName] = useState(row.name);
    const [nameErrorText, setNameErrorText] = useState("");

    const [uuid, setUUID] = useState(row.component_uuid);
    const [uuidErrorText, setUuidErrorText] = useState("");

    const [truthSource, setTruthSource] = useState(row.truth_source);
    const [description, setDescription] = useState(row.description);
    const [physicalLocation, setPhysicalLocation] = useState(row.physical_location);
    const [manufacturer, setManufacturer] = useState(row.manufacturer);
    const [model, setModel] = useState(row.model);
    const [isSensor, setIsSensor] = useState(row.is_sensor ? true : false);

    const [lat, setLat] = useState(row.lat);
    const [latErrorText, setLatErrorText] = useState("");

    const [lon, setLon] = useState(row.lon);
    const [lonErrorText, setLonErrorText] = useState("");

    const [radius, setRadius] = useState(row.radius);
    const [radiusErrorText, setRadiusErrorText] = useState("");

    const [sensorType, setSensorType] = useState(row.sensor_type || "NONE");
    const [sensorTypeErrorText, setSensorTypeErrorText] = useState("");

    const [minAngle, setMinAngle] = useState(row.min_angle);
    const [maxAngle, setMaxAngle] = useState(row.max_angle);

    const [selectedHimsMonitors, setSelectedHimsMonitors] = useState([]);
    const [availableHimsMonitors, setAvailableHimsMonitors] = useState([]);

    useEffect(() => {
        const availableMonitors = props.allHimsMonitors.filter((monitor) => {
            return row.sensor_type === monitor.source_type;
        });
        setAvailableHimsMonitors(availableMonitors);
    }, []);

    useEffect(() => {
        if (Array.isArray(row.monitor_uuids) === false || row.monitor_uuids.length === 0) {
            return;
        }
        const selectedMonitors = props.allHimsMonitors.filter((monitor) => {
            return row.monitor_uuids.includes(monitor.monitor_uuid);
        });
        setSelectedHimsMonitors(selectedMonitors);
    }, []);

    const handleSensorChange = async () => {
        setIsSensor(!isSensor);
    };
    const handleSubmit = () => {
        if (!name) {
            return setNameErrorText("Please enter a valid name.");
        } else if (nameErrorText) {
            setNameErrorText("");
        }
        if (IsInvalidUUID(uuid)) {
            return setUuidErrorText("Please enter a valid UUID.");
        } else if (uuidErrorText) {
            setUuidErrorText("");
        }
        if (row.component_uuid != uuid && props.components.some((s) => s.component_uuid === uuid)) {
            return setUuidErrorText("Please enter a unique UUID.");
        } else if (uuidErrorText) {
            setUuidErrorText("");
        }
        const latitudeDeg = parseFloat(lat);
        const longitudeDeg = parseFloat(lon);
        const radiusMeters = parseFloat(radius);

        const minAngleDeg = parseFloat(minAngle);
        const maxAngleDeg = parseFloat(maxAngle);

        if (isSensor) {
            if (isNaN(latitudeDeg) || latitudeDeg < -90 || latitudeDeg > 90) {
                return setLatErrorText("Please enter a valid latitude.");
            } else if (latErrorText) {
                setLatErrorText("");
            }
            if (isNaN(longitudeDeg) || longitudeDeg < -180 || longitudeDeg > 180) {
                return setLonErrorText("Please enter a valid longitude.");
            } else if (lonErrorText) {
                setLonErrorText("");
            }
            if (isNaN(radiusMeters) || radiusMeters <= 0 || Number.isInteger(radiusMeters) === false) {
                return setRadiusErrorText("Please enter a whole number greater than 0.");
            } else if (radiusErrorText) {
                setRadiusErrorText("");
            }
            if (sensorType === "NONE") {
                return setSensorTypeErrorText("Please select a sensor type.");
            } else if (sensorTypeErrorText) {
                setSensorTypeErrorText("");
            }
        }
        const monitorUuids = selectedHimsMonitors.map((monitor) => {
            return monitor.monitor_uuid;
        });
        const newRow = {
            id: row.id,
            component_uuid: uuid,
            name: name,
            truth_source: truthSource,
            description: description,
            physical_location: physicalLocation,
            manufacturer: manufacturer,
            model: model,
            is_sensor: isSensor,
            sensor_type: sensorType === "NONE" ? "" : sensorType,
            lat: latitudeDeg,
            lon: longitudeDeg,
            radius: radiusMeters,
            min_angle: isNaN(minAngleDeg) ? null : minAngleDeg,
            max_angle: isNaN(maxAngleDeg) ? null : maxAngleDeg,
            monitor_uuids: monitorUuids
        };
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newRow)
        };
        fetch("api/hms/component/insertupdate", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                fetchSensors();
                handleClose();
            })
            .catch((err) => handleFailedFetch(err));
    };
    const handleClose = () => {
        props.handleComponentDialogClose();
    };
    const handleSensorTypeChange = (e) => {
        const newSensorType = e.target.value;
        setSensorType(newSensorType);

        const availableMonitors = props.allHimsMonitors.filter((monitor) => {
            return newSensorType === monitor.source_type;
        });
        setAvailableHimsMonitors(availableMonitors);
    };
    return (
        <Dialog onClose={handleClose} open={props.componentDialogOpen} maxWidth="sm">
            <DialogTitle>HIMS Component Manager</DialogTitle>
            <DialogContent sx={{ pb: 0, pt: 0 }}>
                <TextField
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    margin="normal"
                    id="name"
                    label="Name"
                    fullWidth
                    error={nameErrorText.length > 0 ? true : false}
                    helperText={nameErrorText}
                />
                <TextField
                    value={uuid}
                    onChange={(e) => setUUID(e.target.value)}
                    margin="normal"
                    id="uuid"
                    label="UUID"
                    fullWidth
                    error={uuidErrorText.length > 0 ? true : false}
                    helperText={uuidErrorText}
                />
                <TextField
                    value={truthSource}
                    onChange={(e) => setTruthSource(e.target.value)}
                    margin="normal"
                    id="truthsource"
                    label="Truth Source"
                    fullWidth
                />
                <TextField
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    margin="normal"
                    id="description"
                    label="Description"
                    fullWidth
                />
                <TextField
                    value={physicalLocation}
                    onChange={(e) => setPhysicalLocation(e.target.value)}
                    margin="normal"
                    id="physical Location"
                    label="Physical Location"
                    fullWidth
                />
                <TextField
                    value={manufacturer}
                    onChange={(e) => setManufacturer(e.target.value)}
                    margin="normal"
                    id="manufacturer"
                    label="Manufacturer"
                    fullWidth
                />
                <TextField
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    margin="normal"
                    id="model"
                    label="Model"
                    fullWidth
                    inputProps={{ "data-testid": "model" }}
                />
                <FormControlLabel control={<Checkbox checked={isSensor} onChange={handleSensorChange} id="isVisible" />} label="Is Sensor?" />

                {isSensor ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    value={lat}
                                    onChange={(e) => setLat(e.target.value)}
                                    id="lat"
                                    label="Lat"
                                    fullWidth
                                    inputProps={{ "data-testid": "lat" }}
                                    error={latErrorText.length > 0 ? true : false}
                                    helperText={latErrorText}
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    value={lon}
                                    onChange={(e) => setLon(e.target.value)}
                                    id="lon"
                                    label="Lon"
                                    fullWidth
                                    inputProps={{ "data-testid": "lon" }}
                                    error={lonErrorText.length > 0 ? true : false}
                                    helperText={lonErrorText}
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    value={radius}
                                    onChange={(e) => setRadius(e.target.value)}
                                    id="radius"
                                    label="Radius"
                                    fullWidth
                                    inputProps={{ "data-testid": "radius" }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                                    error={radiusErrorText.length > 0 ? true : false}
                                    helperText={radiusErrorText}
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    value={minAngle}
                                    onChange={(e) => setMinAngle(e.target.value)}
                                    id="minAngle"
                                    label="Min Angle (Optional)"
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <TextField
                                    value={maxAngle}
                                    onChange={(e) => setMaxAngle(e.target.value)}
                                    id="maxAngle"
                                    label="Max Angle (Optional)"
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <FormControl fullWidth error={sensorTypeErrorText.length > 0 ? true : false}>
                                    <InputLabel id="sensor-type-label">Sensor Type</InputLabel>
                                    <Select
                                        value={sensorType}
                                        onChange={handleSensorTypeChange}
                                        id="sensor type"
                                        label="Sensor Type"
                                        labelId="sensor-type-label"
                                        fullWidth
                                    >
                                        <MenuItem value="NONE">NONE</MenuItem>
                                        <MenuItem value="ADS_B" id="adsb">
                                            ADS_B
                                        </MenuItem>
                                        <MenuItem value="GROUND_BASED_RADAR" id="gbradar">
                                            GROUND_BASED_RADAR
                                        </MenuItem>
                                    </Select>
                                    <FormHelperText>{sensorTypeErrorText}</FormHelperText>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Autocomplete
                            multiple
                            options={availableHimsMonitors}
                            getOptionLabel={(monitor) => `${monitor.monitor_name}`}
                            renderInput={(params) => (
                                <TextField {...params} label="Monitors" placeholder="Search for a monitor" inputProps={{ ...params.inputProps }} fullWidth />
                            )}
                            onChange={(e, value) => setSelectedHimsMonitors(value)}
                            value={selectedHimsMonitors}
                            fullWidth
                        />
                    </Box>
                ) : (
                    <></>
                )}
            </DialogContent>
            <DialogActions>
                <Button sx={{ mr: "auto" }} onClick={handleClose} data-testid="close" id="close">
                    Close
                </Button>
                <Button onClick={handleSubmit} data-testid="save" id="save">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
